import {
  sendRefundApproval,
  getRefundDetails,
} from "../redux/actions/refund.action";
import constant from "./CommonConstants";
import { APPROVAL_STATUS } from "../utils/refundConstants";

export const isRefundRequestManager = (designationId) => {
  const env = process.env.BUILD_ENV;
  return constant.refundRequestDesignations[env].manager.includes(designationId);
};

export const isRefundRequestAgent = (designationId) => {
  const env = process.env.BUILD_ENV;
  return constant.refundRequestDesignations[env].agent.includes(designationId);
};

export const formatAmount = (amount) => {
  if (amount === undefined || amount === null) {
    return "N/A";
  }
  const numAmount = parseFloat(amount);
  if (isNaN(numAmount)) {
    return "N/A";
  }
  return numAmount.toFixed(2);
};

export const formatDate = (dateString) => {
  // Check for null, undefined, empty string, or "N/A"
  if (!dateString || dateString === "N/A") {
    return "N/A";
  }

  // If the date is already in DD/MM/YYYY format, return as is
  if (typeof dateString === "string" && /^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
    return dateString;
  }

  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    return "N/A";
  }

  return date.toLocaleDateString("en-GB");
};

// Helper function to format package names
export const formatPackageName = (packageName) => {
  if (!packageName) {
    return "N/A";
  }

  return packageName.replace("-owner", "");
};

export const submitRefundApprovalWithToggle = async (approvalData, dispatch) => {
  return await dispatch(sendRefundApproval(approvalData));
};

export const getRefundDetailsWithAttachments = async (refundId, dispatch) => {
  return await dispatch(getRefundDetails(refundId));
};

// Helper function to format all entry properties
export const formatEntryData = (entry) => {
  return {
    ...entry,
    ratePrice: formatAmount(entry.ratePrice),
    salesPrice: formatAmount(entry.salesPrice),
    refundAmount: formatAmount(entry.refundAmount),
    revenueLoss: formatAmount(entry.revenueLoss),

    packageName: formatPackageName(entry.packageName),

    invoiceDate: formatDate(entry.invoiceDate),
    startDate: formatDate(entry.startDate),
    endDate: formatDate(entry.endDate),

    daysConsumed:
      entry.daysConsumed !== undefined && entry.daysConsumed !== null
        ? entry.daysConsumed.toString()
        : "N/A",
  };
};

export const getUniqueAgentRemarks = (entries) => {
  const remarks = entries
    .filter((entry) => entry.approvalRemarks && entry.approvalRemarks.trim())
    .map((entry) => entry.approvalRemarks.trim());

  return [...new Set(remarks)];
};

export const getUniqueAttachments = (entries) => {
  const allAttachments = entries.reduce((acc, entry) => {
    if (entry.attachments && entry.attachments.length > 0) {
      acc.push(...entry.attachments);
    }
    return acc;
  }, []);

  const uniqueAttachments = [];
  const seenIds = new Set();

  allAttachments.forEach((attachment) => {
    const id = attachment.id || attachment.mediaId;
    if (!seenIds.has(id)) {
      uniqueAttachments.push(attachment);
      if (id) seenIds.add(id);
    }
  });

  return uniqueAttachments;
};

export const isAnyPackagePending = (packagesEntries = []) => {
  // Check if any package has pending status
  return packagesEntries.some(
    (pkg) => pkg.approvalStatus === APPROVAL_STATUS.PENDING_APPROVAL
  );
};

export const extractRefundOpportunityData = (packagesData = {}) => {
  const { gstLossCase: isGstLossCase, entries = [] } = packagesData;
  const isStatusPending = isAnyPackagePending(entries);
  return { ...packagesData, isGstLossCase, isStatusPending };
};

export const convertMediaIdsToAttachments = async (
  mediaIds,
  dispatch,
  getOpenableUrlsFromMediaIds = () => {},
  options = {}
) => {
  const {
    fileNamePrefix = "Attachment",
    section = "common",
    includePendingFlag = false,
    useFileName = false,
  } = options;

  if (!mediaIds || mediaIds.length === 0) {
    return [];
  }

  try {
    const openableUrls = await dispatch(getOpenableUrlsFromMediaIds(mediaIds));

    // Convert openable URLs to attachment objects
    return openableUrls.map((urlData, index) => ({
      id: urlData.id,
      name: useFileName ? urlData.fileName : `${fileNamePrefix} ${index + 1}`,
      fileName: useFileName ? urlData.fileName : `${fileNamePrefix} ${index + 1}`,
      absolute_url: urlData.absoluteUrl,
      url: urlData.url,
      sizeInBytes: urlData.sizeInBytes,
      section,
      ...(includePendingFlag && { isPendingAttachment: true }),
    }));
  } catch (error) {
    // Fallback: create basic attachment objects with media IDs
    return mediaIds.map((mediaId, index) => ({
      id: mediaId,
      name: useFileName ? `File ${index + 1}` : `${fileNamePrefix} ${index + 1}`,
      fileName: useFileName ? `File ${index + 1}` : `${fileNamePrefix} ${index + 1}`,
      absolute_url: null,
      url: null,
      sizeInBytes: 0,
      section,
      mediaId: mediaId,
      ...(includePendingFlag && { isPendingAttachment: true }),
    }));
  }
};

export const processPendingPackages = async (
  entries,
  dispatch,
  getOpenableUrlsFromMediaIds
) => {
  const pendingPackages = entries.filter(
    (pkg) => pkg.approvalStatus === APPROVAL_STATUS.PENDING_APPROVAL
  );

  let remark = "";
  let attachmentFiles = [];

  const firstPendingPackage = pendingPackages[0];
  if (firstPendingPackage && firstPendingPackage.approvalRemarks) {
    remark = firstPendingPackage.approvalRemarks;
  }

  if (
    firstPendingPackage &&
    firstPendingPackage.attachmentUrls &&
    firstPendingPackage.attachmentUrls.length > 0
  ) {
    attachmentFiles = await convertMediaIdsToAttachments(
      firstPendingPackage.attachmentUrls,
      dispatch,
      getOpenableUrlsFromMediaIds,
      { includePendingFlag: true }
    );
  }

  return {
    selectedPackages: pendingPackages,
    remark,
    attachmentFiles,
  };
};
